import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../../../services/translation.service';
import TextFieldFormGroup from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import ValidationSummary from '../../../Shared/ValidationSummary';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import type IValidatable from '../../../Shared/validatable.interface';
import FileUpload, { type IFileUploadState } from '../../../Shared/FileUpload';
import HoneyPot, { type IHoneyPot } from '../../../Shared/HoneyPot';
import Consent, { type IConsentState } from '../../../Shared/Consent';
import ContactInformationSimple, { type IContactInformationSimpleState } from '../../../Shared/ContactInformationSimple';
import { ContactInformationSimpleField } from '../../../Shared/ContactInformationSimple/contact-information-simple-field.enum';
import FormSubmitResult from '../FormSubmitResult';
import ToggleablePanel from '../../../Shared/ToggleablePanel';
import FeedbackRequested from '../FeedbackRequested';
import { AnalyticsService } from '../../../../services/analytics-service';
import { LanguageService } from '../../../../services/language.service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import type { IComplaintApplication } from './models/complaint-application.model';
import TripLegSelector, { type ITripLegSelectorState } from '../../../Shared/TripLegSelector';
import { Status } from '../../../../hooks/fetch.hook';
import AntiforgeryService from '../../../../services/anti-forgery.service';

function ComplaintForm({ onCompletedChange }: { onCompletedChange: (completed: boolean) => void }) {
  // === STATE ===
  const [descriptionState, setDescriptionState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [showTripLegSelector, setShowTripLegSelector] = useState(false);
  const [tripLegSelectorState, setTripLegSelectorState] = useState<ITripLegSelectorState>({
    value: { journey: undefined, tripLeg: undefined, tripLegIndex: undefined },
    validationInfo: new ValidationInfo(),
  });
  const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ value: [], validationInfo: new ValidationInfo() });
  const [feedbackRequested, setFeedbackRequested] = useState(false);
  const [contactInformationState, setContactInformationState] = useState<IContactInformationSimpleState>({
    value: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
    },
    validationInfo: new ValidationInfo(),
  });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const descriptionRef = useRef<IValidatable>(null!);
  const fileUploadRef = useRef<IValidatable>(null!);
  const contactInformationRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: descriptionRef.current, validationInfo: descriptionState.validationInfo, setState: setDescriptionState },
      { component: fileUploadRef.current, validationInfo: fileUploadState.validationInfo, setState: setFileUploadState },
      { component: contactInformationRef.current, validationInfo: contactInformationState.validationInfo, setState: setContactInformationState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;
      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current.isValid()) return;
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSubmitStatus(Status.loading);

      const application: IComplaintApplication = {
        freeText: descriptionState.value,
        isRelatedToJourney: showTripLegSelector,
        plannedJourneyDetailsReference: showTripLegSelector ? tripLegSelectorState.value.journey?.detailsReference : undefined,
        plannedJourneyBrokenTripLegIndex:
          showTripLegSelector && tripLegSelectorState.value.tripLeg ? tripLegSelectorState.value.journey?.tripLegs?.indexOf(tripLegSelectorState.value.tripLeg) : undefined,
        isFeedbackRequested: feedbackRequested,
        firstName: contactInformationState.value.firstName,
        lastName: contactInformationState.value.lastName,
        phoneNumber: contactInformationState.value.phoneNumber,
        email: contactInformationState.value.email,
        isConsentGiven: consentState.value,
      };
      const formData = new FormData();
      formData.append('complaintInformation', JSON.stringify(application));
      fileUploadState.value.forEach((file, index) => {
        formData.append(`files[${index}]`, file.actualFile);
      });
      try {
        const response = await fetch(`/api/questionsandopinions/complaint?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          onCompletedChange(true);
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, 'Skickat fråga', '');
        } else {
          if (response.status === 400) {
            const responseBody = await response.json();
            if (!responseBody.errors) throw new Error('Missing errors');
            handleServerValidationErrors(responseBody.errors);
          }
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, 'Skickat fråga - misslyckat', '');
        }
      } catch {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: undefined };
        onCompletedChange(true);
      }
    });
    setTimeout(() => {
      document.getElementById('questionsandopinions')?.scrollIntoView(true);
      document.getElementById(!isFormValid || submitResult?.errorCode === 400 ? 'complaint-form-validation-summary' : 'complaint-form-submit-result')?.focus();
    }, 100);
  }

  return (
    <div id="complaint-form" className="complaint-form">
      <div hidden={!(submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400))}>
        <h2 className="d-block d-md-none">{TranslationService.get('questionsandopinionspage.complaint.heading')}</h2>
        {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
        <ValidationSummary id="complaint-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
        <TextFieldFormGroup
          ref={descriptionRef}
          required={true}
          renderAsTextArea={true}
          maxLength={2000}
          labelText={TranslationService.get('questionsandopinions.complaint.subheading')}
          rows={5}
          inputId={'complaint-description'}
          serverValidationName="freeText"
          state={descriptionState}
          onChange={(value) => {
            setDescriptionState((ps) => ({ ...ps, value }));
          }}
          onKeyUp={() => {
            setDescriptionState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
          }}
        />
        <hr className="my-4" />
        <ToggleablePanel
          id="complaint-toggleable-panel"
          panelVisible={showTripLegSelector}
          toggleText={TranslationService.get('questionsandopinions.complaint.fromtoheading')}
          onToggleChange={setShowTripLegSelector}
        >
          <TripLegSelector
            id={'complaint-trip-leg-selector'}
            state={tripLegSelectorState}
            onChange={(value) => {
              setTripLegSelectorState((ps) => ({ ...ps, value }));
            }}
          />
        </ToggleablePanel>
        <hr className="my-4" />
        <FileUpload
          id="complaint-file-upload"
          ref={fileUploadRef}
          state={fileUploadState}
          onFilesAdded={(files) => {
            setFileUploadState((ps) => ({ ...ps, value: [...ps.value, ...files] }));
          }}
          onFileRemoved={(file) => {
            setFileUploadState({ ...fileUploadState, value: fileUploadState.value.filter((f) => f !== file), validationInfo: new ValidationInfo() });
          }}
        />
        <hr className="my-4" />
        <FeedbackRequested id="complaint-feedback-requested" feedbackRequested={feedbackRequested} onFeedbackRequestedChange={setFeedbackRequested} className="mb-3" />
        <ContactInformationSimple
          id="complaint-contact-information"
          ref={contactInformationRef}
          firstNameRequired={feedbackRequested}
          emailRequired={feedbackRequested}
          className="mb-4"
          state={contactInformationState}
          onChange={(value) => {
            setContactInformationState((ps) => ({ ...ps, value }));
          }}
          onKeyUp={(e) => {
            if (e === ContactInformationSimpleField.firstName) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  firstName: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.lastName) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  lastName: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.phoneNumber) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  phoneNumber: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.email) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  email: new ValidationInfo(),
                }),
              }));
            }
          }}
        />
        <Consent
          id="complaint-consent"
          ref={consentRef}
          consentText={TranslationService.get('questionsandopinions.agreementcheckboxtext')}
          integrityPolicyInfo={TranslationService.get('questionsandopinions.agreementinfolinktext')}
          termsAndConditions={TranslationService.get('customerserviceforms.termsandconditions.heading')}
          className="mb-4"
          state={consentState}
          onChange={(value) => {
            setConsentState({ value, validationInfo: new ValidationInfo() });
          }}
        />
        <HoneyPot ref={honeyPotRef} />
        <div className="d-grid">
          <button type="button" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
            <span hidden={submitStatus === Status.loading}>{TranslationService.get('questionsandopinionspage.complaint.submit')}</span>
          </button>
        </div>
      </div>
      <FormSubmitResult
        id="complaint-form-submit-result"
        submitStatus={submitStatus}
        submitErrorCode={submitErrorCode}
        successText={TranslationService.get('questionsandopinions.complaint.thankyoutext')}
        failedText={TranslationService.get('questionsandopinions.failedsubmissiontext')}
        dismissButtonText={TranslationService.get('questionsandopinions.dismissfailedsubmissiontext')}
        onDismiss={() => {
          setSubmitStatus(Status.notperformed);
          onCompletedChange(false);
        }}
      />
    </div>
  );
}

export default ComplaintForm;
